// Pure rules behind recording stock already bought and paid for (Stage 2D;
// F-CTRL-N75): Purchasing "Already bought" and a Day Close stock cash-out.
// Tested in marketLogic.test.ts.
import type { MarketDeliverTo, MarketPaidBy, MarketPurchaseInput, StockCashOutPurchase } from './marketTypes'
import { defaultExpiryFor, expiryMissing } from '@/lib/expiry'

export interface MarketDraftLine {
  stock_item_id: string
  name: string
  unit: string
  qty: number
  unit_cost: string
  lot_no: string
  expiry_date: string
  /** F-CTRL-N73: the item is tracked for expiry, so the line needs a date. */
  tracks_expiry?: boolean
}

/** Where this role may deliver from Purchasing (mirrors marketRoleMayDeliver).
 *  The kitchen manager no longer records purchases (F-CTRL-N75). */
export function marketPlacesFor(role: string): MarketDeliverTo[] {
  if (role === 'admin' || role === 'inventory_manager') return ['store', 'kitchen']
  return []
}

/** Who can pay for an already-bought purchase, first one the default
 *  (mirrors marketRoleMayPayFromTill, pilot Batch 5). The petty-cash tin comes
 *  first because it works without an open day; cash from the till is a Day
 *  Close cash movement, so only an admin or a manager gets it. */
export function marketPayersFor(role: string): MarketPaidBy[] {
  const tillOk = role === 'admin' || role === 'manager'
  return tillOk ? ['petty_cash', 'till', 'buyer', 'company'] : ['petty_cash', 'buyer', 'company']
}

/** `today` (business date, YYYY-MM-DD) prefills a tracked item's expiry from
 *  its shelf life. */
export function addMarketLine(
  lines: MarketDraftLine[],
  it: { id: string; name: string; unit: string; tracks_expiry?: boolean; shelf_life_days?: number | null },
  qty = 1,
  today = '',
): MarketDraftLine[] {
  const i = lines.findIndex((l) => l.stock_item_id === it.id)
  if (i >= 0) return lines.map((l, j) => (j === i ? { ...l, qty: l.qty + qty } : l))
  return [...lines, {
    stock_item_id: it.id, name: it.name, unit: it.unit, qty, unit_cost: '', lot_no: '',
    expiry_date: today ? defaultExpiryFor(it, today) : '',
    tracks_expiry: Boolean(it.tracks_expiry),
  }]
}

const price = (s: string) => {
  const n = Number(s.trim())
  return s.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : null
}

export function marketTotal(lines: MarketDraftLine[]): number {
  return Math.round(lines.reduce((s, l) => s + l.qty * (price(l.unit_cost) ?? 0), 0) * 100) / 100
}

export type MarketProblem = 'noLines' | 'badLine' | 'needExpiry' | 'needBuyer'

export function marketProblem(lines: MarketDraftLine[], paidBy: MarketPaidBy, buyer: string): MarketProblem | null {
  if (lines.length === 0) return 'noLines'
  if (lines.some((l) => !(l.qty > 0) || price(l.unit_cost) === null)) return 'badLine'
  if (lines.some((l) => expiryMissing({ tracks_expiry: l.tracks_expiry }, l.expiry_date))) return 'needExpiry'
  if (paidBy === 'buyer' && !buyer.trim()) return 'needBuyer'
  return null
}

export function marketBody(d: {
  deliverTo: MarketDeliverTo; paidBy: MarketPaidBy; buyer: string; supplier: string; notes: string
  lines: MarketDraftLine[]; photos: { content_type: string; data_base64: string }[]
  /** A real vendor; blank means a market stall (the generic Market vendor). */
  supplierId?: string
}): MarketPurchaseInput {
  const opt = (s: string) => (s.trim() ? s.trim() : undefined)
  return {
    deliver_to: d.deliverTo,
    paid_by: d.paidBy,
    ...(d.supplierId ? { supplier_id: d.supplierId } : {}),
    buyer_name: d.paidBy === 'buyer' ? opt(d.buyer) : undefined,
    supplier_name: opt(d.supplier),
    notes: opt(d.notes),
    lines: d.lines.map((l) => ({
      stock_item_id: l.stock_item_id, qty: l.qty, unit_cost: price(l.unit_cost) ?? 0,
      ...(l.lot_no.trim() ? { lot_no: l.lot_no.trim() } : {}),
      ...(l.expiry_date ? { expiry_date: l.expiry_date } : {}),
    })),
    ...(d.photos.length ? { photos: d.photos } : {}),
  }
}

/** The items of a Day Close stock cash-out (who paid is always the till). */
export function stockCashOutBody(d: {
  deliverTo: MarketDeliverTo; supplier: string; notes: string
  lines: MarketDraftLine[]; photos: { content_type: string; data_base64: string }[]
}): StockCashOutPurchase {
  const { paid_by: _paidBy, buyer_name: _buyer, ...rest } = marketBody({ ...d, paidBy: 'till', buyer: '' })
  return rest
}

/** How far a cash-out's items may differ from the cash before someone has to
 *  say why. Mirrors itemsTolerance in handlers/dayops.go. */
export function itemsTolerance(amount: number): number {
  return Math.max(1, amount * 0.02)
}

export function itemsDiffer(cash: number, itemsTotal: number): boolean {
  return Math.abs(itemsTotal - cash) > itemsTolerance(cash)
}

/** Stock cash-outs whose items were never added. */
export function countNeedingItems(movements: { needs_items?: boolean }[] | undefined): number {
  return (movements ?? []).filter((m) => m.needs_items).length
}
